import { FC, CSSProperties, useMemo, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useS3ImageUploader } from 'hooks/useS3ImageUploader';
import { Button } from 'components/Core/Button';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { DatePickerComponent } from 'components/Inputs/DatePickerComponents/DatePickerComponent';
import { Switch } from 'components/Core/Switch/Switch';
import { BrandsCataloguesTableTransFields } from 'components/Tables/Brand/BrandsCataloguesTable/types';
import { brandsCataloguesTableTranslations } from 'components/Tables/Brand/BrandsCataloguesTable/translations';
import { IBrandsCatalogueWithPagesWithFile } from 'stores/brands/types';
import { BrandsCataloguesFiltersFormTransFields } from '../BrandsCataloguesFiltersForm/types';
import { brandsCataloguesFiltersFormTranslations } from '../BrandsCataloguesFiltersForm/translations';
// import { UploadImageCallbackType } from 'types/common';
import { brandsCatalogueFormTranslations } from './translations';
import { BrandsCatalogueFormTransFields } from './types';
import { availableRoles } from './constants';
import styles from './brandCatalogueForm.module.scss';
import { ControlledInput } from 'components/Inputs/ControlledInput';

const buttonStyleProp: CSSProperties = {
  width: '100%',
};

const BrandCatalogueForm: FC = observer(() => {
  const {
    brandsStore: {
      brandsCatalogue,
      editBrandCatalogue,
      brandBasicData,
      changeBrandCatalogueApprovedStatus,
      changeBrandCatalogueDublicatedStatus,
    },
    mainStore: { user },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<
    | BrandsCatalogueFormTransFields
    | Extract<BrandsCataloguesTableTransFields, 'changeDuplicatedStatusSuccess'>
    | Extract<BrandsCataloguesFiltersFormTransFields, 'dublicatedStatus' | 'dublicated' | 'notDublicated'>
  >([
    brandsCatalogueFormTranslations.name,
    brandsCataloguesTableTranslations.name,
    brandsCataloguesFiltersFormTranslations.name,
  ]);

  const useFormMethods = useForm<IBrandsCatalogueWithPagesWithFile>({
    defaultValues: brandsCatalogue || {},
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, isSubmitting }, // should create errors conts to update errors when they appears
  } = useFormMethods;

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<IBrandsCatalogueWithPagesWithFile>({
    setValue,
  });

  const { mutate, isLoading } = useMutation(editBrandCatalogue, {
    mutationKey: 'editBrandCatalogue',
    onSuccess: () => {
      showAlert(`edit catalogue success`, 'success');
    },
  });

  const { mutate: changeStatusMutation, isLoading: isLoadingChangeStatus } = useMutation(
    changeBrandCatalogueApprovedStatus,
    {
      mutationKey: 'changeBrandCatalogueApprovedStatus',
      onSuccess: () => {
        showAlert(`change catalogue approved status success`, 'success');
      },
      onError: () => {
        brandsCatalogue && setValue('isApproved', brandsCatalogue.isApproved);
      },
    }
  );

  const { mutate: changeDublicatedStatus, isLoading: isLoadingChangeDublicatedStatus } = useMutation(
    changeBrandCatalogueDublicatedStatus,
    {
      mutationKey: 'changeBrandCatalogueDublicatedStatus',
      onSuccess: () => {
        showAlert(t('changeDuplicatedStatusSuccess', { ns: brandsCataloguesTableTranslations.name }), 'success');
      },
      onError: () => {
        brandsCatalogue && setValue('isDuplicated', brandsCatalogue.isDuplicated);
      },
    }
  );

  const onSwitchIsApproved = useCallback(
    (status: boolean) => {
      setValue('isApproved', status);
      if (!brandsCatalogue) return;
      changeStatusMutation({
        id: brandsCatalogue.id,
        isApproved: status,
      });
    },
    [brandsCatalogue]
  );

  const onSwitchIsDuplicated = useCallback(
    (status: boolean) => {
      setValue('isDuplicated', !status);
      if (!brandsCatalogue) return;
      changeDublicatedStatus({
        id: brandsCatalogue.id,
        isDuplicated: !status,
      });
    },
    [brandsCatalogue]
  );

  const onSubmitHandler = useCallback(
    async (inputData: IBrandsCatalogueWithPagesWithFile) => {
      const { previewUrlFile: file } = inputData;
      try {
        if (file) await uploadImage(file, 'previewUrl', `${brandBasicData?.slug}/newspapers/${brandsCatalogue?.id}`);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { previewUrlFile, ...data } = getValues();
        mutate({ ...inputData, ...data });
      } catch {
        showAlert('Problems with image upload', 'warning');
      }
    },
    [brandsCatalogue, brandBasicData]
  );

  const disabled = useMemo(() => !user || !availableRoles.includes(user.role), [user?.role]);

  return brandsCatalogue ? (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.formSection}`}>
            <ControlledInput
              name='name'
              type='text'
              required
              placeholder={t('name')}
              label={t('name')}
              disabled={disabled}
            />
            <DatePickerComponent
              label={t('startDate')}
              name='startDate'
              placeholder={t('startDate')}
              buttonStyleProp={buttonStyleProp}
              stylesProp={{ marginBottom: 24 }}
              maxDate={watch('expiredDate') ? new Date(watch('expiredDate')) : undefined}
              required
              disabled={disabled}
            />
            <DatePickerComponent
              label={t('expiredDate')}
              name='expiredDate'
              placeholder={t('expiredDate')}
              buttonStyleProp={buttonStyleProp}
              stylesProp={{ marginBottom: 24 }}
              noMaxDate
              minDate={watch('startDate') ? new Date(watch('startDate')) : undefined}
              disabled={!watch('startDate') || disabled}
              required
            />
            <div className={styles.statusRow}>
              <div className={`${styles.statusDot} ${!brandsCatalogue.isActive && styles.inactive}`} />
              <p>{t(brandsCatalogue.isActive ? 'active' : 'inactive')}</p>
            </div>
          </section>
          <section className={styles.rightSection}>
            <ImageUploader
              name='previewUrl'
              title={t('previewUrl')}
              path='brands-catalogs-preview'
              disabled={disabled}
            />
          </section>
        </form>
        <div className={styles.saveBlock}>
          <Button
            title={t('save')}
            onClick={handleSubmit(onSubmitHandler)}
            isDisabled={
              isSubmitting ||
              isLoading ||
              isImageUploading ||
              isLoadingChangeStatus ||
              isLoadingChangeDublicatedStatus ||
              disabled
            }
          />
          <div className={styles.checkboxes}>
            <div className={styles.checkboxWrap}>
              <h6 className={`${styles.title}`}>{t('validStatus')}</h6>
              <Switch
                isActive={!!watch('isApproved')}
                onChange={onSwitchIsApproved}
                disabled={isSubmitting || isLoading || isImageUploading || isLoadingChangeStatus}
              />
            </div>
            <div className={styles.checkboxWrap}>
              <h6 className={`${styles.title}`}>
                {t('dublicatedStatus', { ns: brandsCataloguesFiltersFormTranslations.name })}:{' '}
                {t(watch('isDuplicated') ? 'dublicated' : 'notDublicated', {
                  ns: brandsCataloguesFiltersFormTranslations.name,
                })}
              </h6>
              <Switch
                isActive={!watch('isDuplicated')}
                onChange={onSwitchIsDuplicated}
                disabled={isSubmitting || isLoading || isImageUploading || isLoadingChangeDublicatedStatus}
                classNameProp={styles.isDuplicatedStatus}
              />
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  ) : null;
});

export default BrandCatalogueForm;
